import { useParams } from 'react-router-dom'
import { useState, useEffect, type ComponentType } from 'react'
import type { MDXComponents } from 'mdx/types.js'
import { SEOTags } from '@/components/SEOTags'
import BlogLayout from '@/components/BlogLayout'
import { posts, postComponents } from '@/content/blog/postRegistry'
import { mdxComponents } from '@/components/mdxComponents'
import NotFoundPage from '@/pages/NotFoundPage'

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const [PostComponent, setPostComponent] = useState<ComponentType<{ components?: MDXComponents }> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const meta = posts.find((p) => p.slug === slug)

  useEffect(() => {
    if (!slug) { setNotFound(true); setIsLoading(false); return }
    const loader = postComponents[slug]
    if (!loader) { setNotFound(true); setIsLoading(false); return }
    loader()
      .then((mod) => { setPostComponent(() => mod.default); setIsLoading(false) })
      .catch(() => { setNotFound(true); setIsLoading(false) })
  }, [slug])

  if (notFound) return <NotFoundPage />

  if (isLoading || !meta) {
    return (
      <BlogLayout>
        <div className="animate-pulse space-y-4 pt-4">
          <div className="h-6 bg-gray-200 rounded w-1/4" />
          <div className="h-10 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="mt-8 space-y-3">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-4 bg-gray-200 rounded w-4/6" />
          </div>
        </div>
      </BlogLayout>
    )
  }

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: meta.title,
    description: meta.description,
    datePublished: meta.date,
    author: { '@type': 'Person', name: meta.author },
    publisher: { '@type': 'Organization', name: 'Steadfast HSA', url: 'https://steadfasthsa.com' },
    url: `https://steadfasthsa.com/blog/${meta.slug}`,
  })

  return (
    <BlogLayout>
      <SEOTags
        title={`${meta.title} | Steadfast HSA`}
        description={meta.description}
        url={`https://steadfasthsa.com/blog/${meta.slug}`}
        ogType="article"
        schema={schema}
      />

      {/* Article header */}
      <header className="mb-10">
        <div className="flex flex-wrap gap-2 mb-4">
          {meta.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-[#f0f4f8] px-3 py-1 text-xs text-[#1B365D] font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
        <h1 className="text-4xl font-extrabold text-[#1B365D] mb-4">{meta.title}</h1>
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
          <span>{meta.author}</span>
          <span>·</span>
          <time dateTime={meta.date}>
            {new Date(meta.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
          <span>·</span>
          <span>{meta.readingTime} min read</span>
        </div>
      </header>

      {/* MDX content */}
      <article className="prose prose-slate prose-lg max-w-none
        prose-headings:text-[#1B365D] prose-headings:font-bold
        prose-a:text-[#1B365D] prose-a:underline
        prose-strong:text-[#1B365D]">
        {PostComponent && <PostComponent components={mdxComponents} />}
      </article>
    </BlogLayout>
  )
}
